import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { usePost, usePosts } from "@/hooks/usePosts";
import type { PostSummary } from "@/lib/api/posts";

export function WritingList() {
  const { data, isError, isLoading } = usePosts(true);

  return (
    <main className="min-h-screen px-8 py-24 md:px-24">
      <PageNav />
      <section aria-labelledby="writing-h">
        <h1 id="writing-h" className="mb-12 font-display text-5xl md:text-7xl">
          Tekster
        </h1>
        {isLoading && <p className="text-muted">Laster ...</p>}
        {isError && <p className="text-muted">Kunne ikke laste tekster.</p>}
        {data && data.length === 0 && (
          <p className="max-w-xl text-muted">Ingen innlegg importert ennå.</p>
        )}
        <ol className="space-y-6">
          {data?.map((post) => (
            <WritingItem key={post.id} post={post} />
          ))}
        </ol>
      </section>
    </main>
  );
}

export function WritingPost() {
  const { slug } = useParams();
  const { data, isError, isLoading } = usePost(slug);

  return (
    <main className="min-h-screen px-8 py-24 md:px-24">
      <PageNav />
      {isLoading && <p className="text-muted">Laster ...</p>}
      {isError && <p className="text-muted">Kunne ikke laste innlegget.</p>}
      {data && (
        <article className="max-w-3xl" aria-labelledby="post-h">
          <p className="font-mono text-xs uppercase text-muted">{formatDate(data.published_at)}</p>
          <h1 id="post-h" className="mt-4 font-display text-5xl md:text-7xl">
            {data.title}
          </h1>
          {data.summary && (
            <p className="mt-6 text-lg leading-relaxed text-muted">{data.summary}</p>
          )}
          <div
            className="post-content mt-12 space-y-5 leading-relaxed text-muted"
            dangerouslySetInnerHTML={{ __html: data.content_html }}
          />
        </article>
      )}
    </main>
  );
}

function WritingItem({ post }: { post: PostSummary }) {
  return (
    <motion.li
      className="border-muted/30 border-l-2 pl-6"
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.4 }}
    >
      <Link
        to={`/writing/${post.slug}`}
        className="block hover:text-accent focus-visible:outline-none"
      >
        <p className="font-mono text-xs uppercase text-muted">{formatDate(post.published_at)}</p>
        <h2 className="mt-2 text-2xl font-medium">{post.title}</h2>
        {post.summary && <p className="mt-2 max-w-2xl text-muted">{post.summary}</p>}
      </Link>
    </motion.li>
  );
}

function PageNav() {
  return (
    <nav className="mb-16">
      <Link
        to="/"
        className="font-mono text-xs uppercase text-accent hover:underline focus-visible:underline focus-visible:outline-none"
      >
        Hjem
      </Link>
    </nav>
  );
}

function formatDate(value: string | null) {
  if (!value) return "Udatert";
  return new Intl.DateTimeFormat("nb-NO", {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(new Date(value));
}
